import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { FaTimes } from '../../../../util/reactIcons';
import { setShowHistory } from '../../../../actions/LayoutActions';
import { addSubdirectoryToPath } from '../../../../util/getConfig';
import { buildPatientHistorySoapRequest, parsePatientHistorySoap } from './SoapXmlParser';
import { PatientHistoryCloseButton, PatientHistoryContainer, PatientHistoryMessage } from '../styled/PatientHistory';
import StudiesInfo from './StudiesInfo';
import PatientInfo from './PatientInfo';

export const PatientHistory = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [history, setHistory] = useState(null);
  const [data, setData] = useState(null);

  const {
    DATASOURCE_PATIENT_HISTORY: patientHistoryDatasource,
    WITH_ACCESSION_NUMBER_LOADING: isAccessionNumber,
    PACS_SELECTOR_IN_URL_PARAM: pacsSelectorUrlParam,
  } = useSelector((state) => state.config);

  const endpoint = patientHistoryDatasource?.endpoint;
  const urlParams = patientHistoryDatasource?.urlParams;
  const patientId = useSelector((state) => state.patient.PatientID);
  const soapTemplateUrl = addSubdirectoryToPath(patientHistoryDatasource?.template);
  const urlParamValues = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return Object.fromEntries((Array.isArray(urlParams) ? urlParams : []).map((param) => {
      if (queryParams.has(param)) return [param, queryParams.get(param)];
      if (param === 'Nasi') {
        try {
          return [param, sessionStorage.getItem('Nasi')];
        } catch (error) {
          return [param, null];
        }
      }
      return [param, null];
    }));
  }, [location.search, urlParams]);

  const openSelectedStudy = (study) => {
    const identifier = isAccessionNumber ? study.AccessionNumber : study.StudyInstanceUID;
    const route = isAccessionNumber ? '/viewer/access' : '/viewer/study';
    const datasourceParam = pacsSelectorUrlParam || 'datasource';
    const queryParams = new URLSearchParams({ [datasourceParam]: study.Location });

    window.location.href = `${route}/${identifier}?${queryParams.toString()}`;
  };

  const closeHistory = () => dispatch(setShowHistory(false));

  useEffect(() => {
    if (!endpoint) {
      setData({
        status: 'error',
        message: 'History endpoint not defined',
      });
      return undefined;
    }

    setData({
      status: 'wait',
      message: 'Requesting data...',
    });

    const requestPatientHistory = async () => {
      const templateResponse = await axios.get(soapTemplateUrl, { responseType: 'text' });
      const requestBody = buildPatientHistorySoapRequest(templateResponse.data, {
        PatientID: patientId,
        ...urlParamValues,
      });
      const response = await axios.post(endpoint, requestBody, {
        responseType: 'text',
        headers: {
          'Content-Type': 'text/xml; charset=utf-8',
        },
      });

      return parsePatientHistorySoap(response.data);
    };

    requestPatientHistory()
      .then((patientHistory) => {
        setData({ status: 'ok', message: null });
        setHistory(patientHistory);
      })
      .catch((err) => {
        setData({ status: 'error', message: err.message || 'Error fetching patient history' });
        setHistory(null);
        console.warn(err);
      });

    return undefined;
  }, [endpoint, patientId, soapTemplateUrl, urlParamValues]);

  const CloseButton = () => (
    <PatientHistoryCloseButton onClick={closeHistory}>
      <FaTimes size="1.3em" />
    </PatientHistoryCloseButton>
  );

  if (!data) {
    return (
      <PatientHistoryMessage>
        <CloseButton />
        loading...
      </PatientHistoryMessage>
    );
  }

  if (data.status !== 'ok') {
    return (
      <PatientHistoryMessage>
        <CloseButton />
        {data.message}
      </PatientHistoryMessage>
    );
  }

  return (
    <PatientHistoryContainer>
      <CloseButton />
      {history?.patient && <PatientInfo patient={history.patient} />}
      {history?.studies && <StudiesInfo studies={history.studies} handleClick={openSelectedStudy} />}
    </PatientHistoryContainer>
  );
};

export default PatientHistory;
